import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { Button, VolumeControl } from "@legends/ui";
import {
  fetchCaveRecordings,
  watchRecording,
  startTicketCheckout,
  track,
  ApiError,
  type PublicRecording,
  type RecordingWatch,
} from "./api";
import { AsyncState, formatPrice, type LoadStatus } from "./sections";
import { currentReturnPath } from "./checkoutReturn";
import { useHlsPlayback } from "./hlsPlayback";
import { IS_IOS, loadWatchVolume, loadWatchMuted, saveWatchVolume, saveWatchMuted } from "./watchAudioPrefs";

// Fan-facing on-demand (VOD) list for a cave (ADR 0032, hardened ADR 0109). Lists
// the recordings that are currently available, and plays one on demand — the
// server enforces the per-user view cap + availability window. We surface those
// outcomes (403 cap, 410 window) as graceful messages rather than a broken player,
// and keep loading / empty / error distinct (ADR 0049): a failed list read shows an
// error + Retry instead of silently collapsing into "no recordings".

const panel: CSSProperties = {
  border: "1px solid var(--hairline)",
  borderRadius: "var(--radius-lg)",
  background: "var(--surface-base)",
  padding: 14,
  marginBottom: 10,
};

/** A recording is discoverable only while the server says it's ready AND its
 *  availability window is still open. The public list is already ready + in-window
 *  (server-authoritative), so this is a client backstop against a stale list
 *  (clock skew / cache) surfacing an expired recording that would only 410 on play. */
function isDiscoverable(r: PublicRecording): boolean {
  if (r.availableUntil && new Date(r.availableUntil).getTime() <= Date.now()) return false;
  return true;
}

/** Map a /watch failure to graceful, fan-facing copy — never a broken player. The
 *  server distinguishes 403 (no ticket OR view-limit reached) with a human message,
 *  410 (window closed), and 409 (not yet published). */
function watchErrorMessage(e: unknown): string {
  if (e instanceof ApiError) {
    // The server now sends a curated JSON message (LSC-421); but never surface the
    // generic "API returned N" fallback to a fan if a body ever fails to parse.
    const msg = /^API returned \d+$/.test(e.message) ? "" : e.message;
    switch (e.status) {
      case 403:
        // "a ticket is required to watch" or "you have reached the view limit…"
        return msg || "A ticket is required, or you've reached this recording's view limit.";
      case 410:
        return "This recording is no longer available.";
      case 409:
        return "This recording isn't ready to watch yet.";
      default:
        return msg || "Couldn't start playback. Please try again.";
    }
  }
  return "Couldn't start playback. Please try again.";
}

/** VodPlayer renders a granted recording watch. For a segmented HLS VOD grant
 *  (mode:"hls", ADR 0229) it plays the playlist via useHlsPlayback — which gives
 *  instant long-form startup + AirPlay/PiP/Cast on a plain <video> (reusing the live
 *  HLS player) — and falls back to the progressive MP4 (mp4Url) if HLS can't play.
 *  For "stream"/"url" grants it plays the MP4 directly, exactly as before. */
function VodPlayer({ watch, onError }: { watch: RecordingWatch; onError: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [hlsFailed, setHlsFailed] = useState(false);
  const hlsActive = watch.mode === "hls" && !hlsFailed;
  // When HLS fails fatally, drop to the progressive MP4 (mp4Url, else url) rather
  // than a broken player — a web-only resilience net over the server fallback. The
  // error handler is memoized so a parent re-render (the cave live poll, LSC-419)
  // can't hand useHlsPlayback a fresh callback and retrigger an HLS teardown/rebuild
  // that would restart playback at 0:00.
  const onHlsError = useCallback(() => setHlsFailed(true), []);
  // VOD → lowLatency:false: a replay is a finished VOD playlist, not a live edge.
  useHlsPlayback(videoRef, hlsActive ? watch.url : null, hlsActive, onHlsError, false);
  // The <video> src is managed by the hook while HLS is active; otherwise it's the
  // direct MP4 (the fallback after an HLS failure, or a plain stream/url grant).
  const directSrc = hlsActive ? undefined : hlsFailed ? watch.mp4Url ?? watch.url : watch.url;

  // The fan's OWN volume/mute on the replay player (LSC-544), bound to the <video>
  // and sharing the live-watch preference (watchAudioPrefs — the same sessionStorage
  // keys ConsumerWatch uses), so replay volume is actually controllable and consistent
  // with the live Fan watch (works on HLS and MP4 alike — it's element-level). Native
  // controls stay; a volumechange mirror + an `applying` guard keep custom + native
  // consistent without the two-step write feeding back and clobbering mute.
  const [volume, setVolume] = useState<number>(loadWatchVolume);
  const [muted, setMuted] = useState<boolean>(loadWatchMuted);
  const setVolumePersist = (v: number) => { setVolume(v); saveWatchVolume(v); };
  const setMutedPersist = (m: boolean) => { setMuted(m); saveWatchMuted(m); };
  const applyingRef = useRef(false);
  const applyPrefs = useCallback((el: HTMLVideoElement) => {
    applyingRef.current = true;
    el.volume = Math.min(1, Math.max(0, volume));
    el.muted = muted;
    applyingRef.current = false;
  }, [volume, muted]);
  useEffect(() => {
    const el = videoRef.current;
    if (el) applyPrefs(el);
  }, [applyPrefs, hlsActive, directSrc]);

  return (
    <div style={{ position: "relative" }}>
      <video
        ref={videoRef}
        data-testid="vod-player"
        controls
        autoPlay
        src={directSrc}
        onLoadedMetadata={(e) => applyPrefs(e.currentTarget)}
        onVolumeChange={(e) => {
          if (applyingRef.current) return;
          const el = e.currentTarget;
          if (el.muted !== muted) setMutedPersist(el.muted);
          if (Math.abs(el.volume - volume) > 0.001) setVolumePersist(el.volume);
        }}
        onError={() => {
          // A direct-MP4 error (not HLS, or the MP4 fallback itself) is a genuine
          // playback failure. While HLS is active the element error is handled by the
          // hls hook's onError, so ignore it here.
          if (!hlsActive) onError();
        }}
        style={{ width: "100%", borderRadius: "var(--radius)", background: "#000", display: "block" }}
      />
      {/* The fan's own volume + mute over the replay stage (LSC-544), mirroring the
          live Fan watch (ADR 0064): top-left, mute-only on iOS (ADR 0076). */}
      <div style={{ position: "absolute", left: 10, top: 10, zIndex: 3 }}>
        <VolumeControl
          volume={volume}
          muted={muted}
          onVolumeChange={setVolumePersist}
          onMutedChange={setMutedPersist}
          hideSlider={IS_IOS}
        />
      </div>
    </div>
  );
}

export interface RecordingsOnDemandProps {
  caveId: string;
  signedIn: boolean | null;
  onSignIn?: () => void;
}

export default function RecordingsOnDemand({ caveId, signedIn, onSignIn }: RecordingsOnDemandProps) {
  const [recordings, setRecordings] = useState<PublicRecording[]>([]);
  const [status, setStatus] = useState<LoadStatus>("loading");
  const [playing, setPlaying] = useState<(RecordingWatch & { id: string }) | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [buyId, setBuyId] = useState<string | null>(null);
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    if (!caveId) return;
    setStatus("loading");
    fetchCaveRecordings(caveId)
      .then((recs) => {
        setRecordings(recs);
        setStatus("ready");
      })
      .catch(() => setStatus("error"));
  }, [caveId]);

  useEffect(() => {
    load();
  }, [load]);

  const available = recordings.filter(isDiscoverable);

  // A failed list read shows an error + Retry (ADR 0049) — distinct from the
  // by-design "no recordings → render nothing" case. While loading we render
  // nothing so the section never flashes on a cave that has no VOD at all.
  if (status === "error") {
    return (
      <section aria-label="On demand" style={{ marginTop: 20 }}>
        <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: "0 0 8px" }}>On Demand</h3>
        <AsyncState status="error" errorText="Couldn't load on-demand recordings." onRetry={load} />
      </section>
    );
  }
  if (status !== "ready" || available.length === 0) return null;

  const watch = (rec: PublicRecording) => {
    setNotice("");
    if (!signedIn) {
      onSignIn?.();
      setNotice("Sign in to watch on-demand recordings.");
      return;
    }
    if (busyId) return; // a watch is already in flight — don't double-count a view
    setBusyId(rec.id);
    watchRecording(rec.id)
      .then((w) => {
        setPlaying({ ...w, id: rec.id });
        track("cavecast_viewed", { cavecastId: rec.id, props: { mode: "vod" } });
      })
      .catch((e) => {
        setPlaying(null); // never leave a broken/stale player behind a failed watch
        setNotice(watchErrorMessage(e));
      })
      .finally(() => setBusyId(null));
  };

  // Buy replay access directly from the replay (LSC-551 / ADR 0237): reuse the
  // existing checkout for the recording's hidden replay ticket type. A granted/covered
  // result (free/dev/owned/covered) means the fan is now entitled → refresh the list so
  // the card flips to Watch. A Stripe result redirects out and returns here (ADR 0048);
  // the fan is entitled on return and presses Watch.
  const buy = (rec: PublicRecording) => {
    setNotice("");
    if (!rec.replayTicketTypeId) return;
    if (!signedIn) {
      onSignIn?.();
      setNotice("Sign in to buy replay access.");
      return;
    }
    if (buyId) return;
    setBuyId(rec.id);
    startTicketCheckout(rec.replayTicketTypeId, currentReturnPath())
      .then((res) => {
        if (res.mode === "stripe" && res.url) {
          window.location.assign(res.url);
          return;
        }
        // Granted / already-covered → entitled now; refresh so the card shows Watch.
        load();
        setNotice("You're in — press Watch replay.");
      })
      .catch((e) => {
        if (e instanceof ApiError && e.status === 401) {
          onSignIn?.();
          setNotice("Sign in to buy replay access.");
          return;
        }
        setNotice("Couldn't start checkout. Please try again.");
      })
      .finally(() => setBuyId(null));
  };

  return (
    <section aria-label="On demand" style={{ marginTop: 20 }}>
      <h3 style={{ fontFamily: "var(--font-display)", color: "var(--text)", margin: "0 0 4px" }}>On Demand</h3>
      <p style={{ color: "var(--text-muted)", fontSize: "var(--fs-body-2)", margin: "0 0 12px" }}>
        Cavecasts you can replay. A ticket is required, and each has its own viewing limit.
      </p>

      {notice && (
        <p role="alert" style={{ color: "var(--ember)", fontSize: "var(--fs-body-2)" }}>
          {notice}
        </p>
      )}

      {playing && (
        <div style={{ ...panel, borderColor: "var(--gold)" }}>
          <VodPlayer
            key={playing.id}
            watch={playing}
            onError={() => {
              setPlaying(null);
              setNotice("Playback failed — please try again.");
            }}
          />
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 8, fontSize: "var(--fs-2xs)", color: "var(--text-muted)" }}>
            <span>
              {playing.viewsRemaining < 0
                ? "Unlimited views"
                : `${playing.viewsRemaining} view${playing.viewsRemaining === 1 ? "" : "s"} left`}
            </span>
            <button type="button" onClick={() => setPlaying(null)} style={{ background: "none", border: "none", color: "var(--gold)", cursor: "pointer" }}>
              Close
            </button>
          </div>
        </div>
      )}

      {available.map((rec) => (
        <div key={rec.id} style={panel}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10 }}>
            <div>
              <strong style={{ color: "var(--text)", fontFamily: "var(--font-display)" }}>{rec.title}</strong>
              {rec.eventStartsAt && (
                <div style={{ fontSize: "var(--fs-2xs)", color: "var(--text-muted)" }}>
                  {new Date(rec.eventStartsAt).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                </div>
              )}
              <div style={{ fontSize: "var(--fs-2xs)", color: "var(--text-dim)" }}>
                {rec.availableUntil ? `Available until ${new Date(rec.availableUntil).toLocaleDateString()}` : "Available now"}
                {rec.maxViewsPerUser > 0 ? ` · ${rec.maxViewsPerUser} views per fan` : ""}
              </div>
            </div>
            {/* Watch when the fan is entitled (or when we don't yet know — the server
                enforces, and the graceful 403 path covers a miss). Buy Ticket — $X when
                the replay is for sale and the signed-in fan is not entitled (LSC-551). */}
            {signedIn && rec.entitled === false && rec.replaySaleEnabled && rec.replayTicketTypeId ? (
              <Button variant="accent" disabled={buyId === rec.id} onClick={() => buy(rec)}>
                {buyId === rec.id ? "Starting…" : `Buy Ticket — ${formatPrice(rec.replayPriceCents ?? 0)}`}
              </Button>
            ) : (
              <Button variant="accent" disabled={busyId === rec.id} onClick={() => watch(rec)}>
                {busyId === rec.id ? "Starting…" : "Watch on demand"}
              </Button>
            )}
          </div>
        </div>
      ))}
    </section>
  );
}
